import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { MobileSidebar } from './MobileSidebar'
import { Header } from './Header'
import { MobileNav } from './MobileNav'
import { PullToRefresh } from './PullToRefresh'
import { OfflineBanner } from './OfflineBanner'
import { QueuedPhotosBanner } from '@/components/offline/QueuedPhotosBanner'
import { SoftphoneWidget } from '@/components/intake/SoftphoneWidget'
import { PlanLockedPage } from '@/components/billing/PlanLockedPage'
import { useIsPlanLocked } from '@/hooks/use-plan-access'

// The work-order detail screen is a full-height, internally-scrolling tasks view.
// On mobile the bottom nav overlaps its last sub-task, so we hide the nav here and
// let the page use the full height (the in-page "Work Orders" back button covers nav).
function isWorkOrderDetail(pathname: string): boolean {
  const m = pathname.match(/^\/work-orders\/([^/]+)$/)
  return !!m && !['new', 'pipeline', 'archive'].includes(m[1])
}

// Routes where pull-to-refresh should be suppressed — typically form-heavy
// setup flows where an accidental pull would wipe in-progress input, or
// crew task / WO detail (explicit Refresh button in the header, and notes
// in progress). The crew week view itself keeps the hold-to-refresh.
const PULL_TO_REFRESH_DISABLED_PATHS = ['/inspections/new', '/portal/crew']
// Crew week views (own + member preview) — exempt from the crew prefix above.
const CREW_WEEK_PATH = /^\/portal\/crew(\/member\/[^/]+)?\/?$/

export function AppShell() {
  const { pathname } = useLocation()
  const pullDisabled =
    !CREW_WEEK_PATH.test(pathname) &&
    PULL_TO_REFRESH_DISABLED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
  const planLocked = useIsPlanLocked(pathname)
  const hideMobileNav = isWorkOrderDetail(pathname) && !planLocked

  return (
    // Pinned to the viewport so only the inner content pane scrolls. With a
    // scrollable document (h-screen on iOS), focus jumps after a reorder could
    // scroll the page itself and leave the bottom nav floating mid-list.
    <div className="fixed inset-0 flex overflow-hidden">
      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Sidebar Drawer */}
      <MobileSidebar />

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />

        <OfflineBanner />
        <PullToRefresh className="min-h-0 flex-1 overflow-auto overscroll-contain" disabled={pullDisabled}>
          {/* One choke point for plan gating: every protected route renders
              through this Outlet, so a locked page swaps its body here while
              the nav, the URL, and the back button stay exactly as they are. */}
          {planLocked ? <PlanLockedPage /> : <Outlet />}
        </PullToRefresh>

        {/* Mobile Bottom Navigation — hidden on the full-screen WO detail so it
            doesn't cover the last sub-task. */}
        {!hideMobileNav && <MobileNav />}
      </div>

      {/* Global browser softphone (renders only when calling is configured) */}
      <SoftphoneWidget />

      {/* Floating banner + drawer for photos still queued to upload. Renders
          nothing when the outbox is empty. */}
      <QueuedPhotosBanner />
    </div>
  )
}
